import React, { forwardRef } from "react"
import { Button, ButtonProps } from "antd"

interface DataQButtonProps {
  id?: string
  isDisabled?: boolean
  isLoading?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  type?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "default"
    | "dashed"
    | "link"
    | "text"
    | "form"
  className?: string
  htmlType?: "button" | "submit" | "reset"
  size?: "large" | "middle" | "small"
  /** The button's accessible name, when its text alone doesn't say which item it acts on. */
  "aria-label"?: string
  children?: React.ReactNode
}

type DataQButtonType = DataQButtonProps["type"]

/** The antd type and danger flag each DataQButton type maps to. */
const ANTD_VARIANT: Record<
  NonNullable<DataQButtonType>,
  { type: ButtonProps["type"]; danger?: boolean }
> = {
  primary: { type: "primary" },
  success: { type: "primary" },
  form: { type: "primary" },
  secondary: { type: "default" },
  default: { type: "default" },
  danger: { type: "default", danger: true },
  dashed: { type: "dashed" },
  link: { type: "link" },
  text: { type: "text" },
}

/**
 * The house button: a thin wrapper over the antd Button. Its look (28px,
 * 12px/600, brand primary, white secondary) comes from the app theme
 * (theme/dataqTheme.ts), so it adds no colour or size classes of its own.
 * "success" and "form" are plain primaries; "danger" is antd's danger default.
 * Forwards its ref so antd tooltips work on it.
 */
const DataQButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  DataQButtonProps
>((props, ref) => {
  const {
    id,
    isDisabled,
    isLoading,
    onClick,
    type,
    className,
    htmlType,
    size,
    children,
  } = props

  const variant = ANTD_VARIANT[type ?? "default"] ?? ANTD_VARIANT.default

  return (
    <Button
      ref={ref}
      id={id}
      disabled={isDisabled}
      loading={isLoading}
      onClick={onClick}
      type={variant.type}
      danger={variant.danger}
      className={`${className || ""} pointer-events-auto`.trim()}
      htmlType={htmlType}
      size={size}
      aria-label={props["aria-label"]}
    >
      {children}
    </Button>
  )
})

DataQButton.displayName = "DataQButton"

export default DataQButton
